import { BsMetaUtil, StrategyExpandedInfo, useSupportedCollateral } from "@bridgesplit/abf-react";
import { Icon, MenuItemProps, Row, Text, TextColor, TextVariant } from "@bridgesplit/ui";
import { formatAddress, formatStrategyAddress } from "@bridgesplit/utils";
import { useNavigate } from "react-router-dom";
import { AppDialog, useAppDialog } from "app/utils";

import { useUpdateStrategyCollateral } from "./terms/util";
import { getStrategyLoansPath, isStrategyMissingUpdateTransaction } from "../common";

export function getStrategyName(strategyExpanded: StrategyExpandedInfo | undefined): string {
    if (!strategyExpanded) return "";
    const principalSymbol = BsMetaUtil.getSymbol(strategyExpanded.principalMetadata);
    return `${principalSymbol} ${formatAddress(strategyExpanded.strategy.address)}`;
}

export function StrategyName({
    strategy,
    variant = "body1",
    addressVariant = "body2",
    addressColor = "caption"
}: {
    strategy: StrategyExpandedInfo;
    variant?: TextVariant;
    addressVariant?: TextVariant;
    addressColor?: TextColor;
}) {
    const principalSymbol = BsMetaUtil.getSymbol(strategy.principalMetadata);
    const address = formatStrategyAddress(strategy.strategy.address);
    return (
        <Row spacing={1}>
            <Text variant={variant}>{principalSymbol}</Text>
            <Text variant={addressVariant} color={addressColor}>
                {address}
            </Text>
        </Row>
    );
}

export function StrategyBalanceStat({
    strategy,
    amount,
    withdraw,
    variant = "body1"
}: {
    strategy: StrategyExpandedInfo | undefined;
    amount: number | undefined;
    withdraw?: boolean;
    variant?: TextVariant;
}) {
    const meta = strategy?.principalMetadata;
    const before = strategy?.totalBalance ?? 0;
    const after = Math.max(0, before + (amount ?? 0) * (withdraw ? -1 : 1));
    return (
        <Row loading={!strategy} spaceBetween>
            {strategy && <StrategyName strategy={strategy} variant={variant} />}
            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                <Text variant={variant} color="caption">
                    {BsMetaUtil.formatAmount(meta, before)}
                </Text>
                <Text color="disabled" variant="body2">
                    <Icon type="forward" />
                </Text>
                <Text variant={variant}>{BsMetaUtil.formatAmount(meta, after)}</Text>
            </Row>
        </Row>
    );
}

export function useStrategyActionMenuItemSections(
    strategyExpanded: StrategyExpandedInfo | undefined
): MenuItemProps[][] {
    const { open } = useAppDialog();
    const updateCollateral = useUpdateStrategyCollateral();
    const tokens = useSupportedCollateral(strategyExpanded?.principalMetadata.mint);
    const navigate = useNavigate();
    if (!strategyExpanded) return [];

    const strategyIsMissingUpdate = isStrategyMissingUpdateTransaction(strategyExpanded);

    const primaryActions: MenuItemProps[] = [
        {
            icon: <Icon type="deposit" />,
            text: "Deposit",
            onClick: () => open(AppDialog.StrategyManageEscrow, { strategy: strategyExpanded, side: "deposit" })
        },
        {
            icon: <Icon type="arrow-diagonal" />,
            text: "Withdraw",
            onClick: () => open(AppDialog.StrategyManageEscrow, { strategy: strategyExpanded, side: "withdraw" })
        }
    ];
    const secondaryActions: MenuItemProps[] = [
        {
            icon: <Icon type="edit" />,
            text: AppDialog.StrategyTerms,
            onClick: () => open(AppDialog.StrategyTerms, { strategy: strategyExpanded }),
            hide: strategyIsMissingUpdate
        },
        {
            icon: <Icon type="pie" />,
            text: "Modify collateral",
            onClick: () =>
                open(AppDialog.MultiSelectCollateral, {
                    header: "Modify collateral",
                    collateral: strategyExpanded.collateral.map((c) => c.metadata.mint),
                    tokens,
                    existingStrategy: strategyExpanded,
                    selectedMarketInfoAddress: strategyExpanded.strategy.marketInformation,
                    setCollateralAsync: (collateral) => updateCollateral(strategyExpanded, collateral),
                    principalMint: strategyExpanded?.principalMetadata.mint,
                    isAdvanced: true
                }),
            hide: strategyIsMissingUpdate
        },
        {
            icon: <Icon type="adjust" />,
            text: "Update terms",
            onClick: () =>
                open(AppDialog.UpdateStrategyApyAndCollateral, {
                    token: strategyExpanded.principalMetadata,
                    strategy: strategyExpanded
                }),
            hide: !strategyIsMissingUpdate
        },
        {
            icon: <Icon type="settings" />,
            text: "Position settings",
            onClick: () =>
                open(AppDialog.StrategySettings, {
                    strategy: strategyExpanded
                })
        }
    ];
    const tertiaryActions: MenuItemProps[] = [
        {
            icon: <Icon type="list" />,
            text: "View loans",
            onClick: () => navigate(getStrategyLoansPath(strategyExpanded?.strategy))
        },
        {
            icon: <Icon type="delete" />,
            text: AppDialog.StrategyDelete,
            onClick: () => open(AppDialog.StrategyDelete, { strategy: strategyExpanded })
        }
    ];

    return [primaryActions, secondaryActions, tertiaryActions];
}
